import { Camera, Loader2, Paperclip } from 'lucide-react';
import { useRef, useState, type ChangeEvent } from 'react';
import { Button } from '@/components/ui/button';
import { CameraCaptureDialog } from './CameraCaptureDialog';

/**
 * Os dois caminhos de uma foto, iguais em todo ponto de foto do sistema: **Tirar foto** abre
 * a câmera, e **Anexar** abre o seletor de arquivos — no celular, a galeria.
 *
 * No celular e no tablet, "Tirar foto" é o `<input capture="environment">`: abre direto o
 * aplicativo de câmera do aparelho, na traseira, com foco, flash e a resolução inteira do
 * sensor, e sem depender da permissão de câmera do navegador. No computador o `capture` é
 * ignorado — o botão abriria o explorador de arquivos, que é o "Anexar" de novo —, e por isso
 * lá ele abre a webcam pelo `CameraCaptureDialog`.
 *
 * Os dois entregam `File`, e daí em diante é um caminho só: quem monta os botões envia (ou
 * guarda no rascunho) sem saber de onde a foto veio. Os campos de arquivo ficam escondidos e
 * os botões os abrem, para os dois caminhos terem a mesma cara.
 */
export interface PhotoSourceButtonsProps {
  /** O que o "Anexar" aceita — o mesmo que o ponto já aceitava (a galeria aceita PDF). */
  readonly accept: string;
  readonly disabled: boolean;
  /** Enviando: o "Tirar foto" gira e diz "Enviando…". */
  readonly isPending?: boolean | undefined;
  /** No meio de um formulário, os dois discretos; fora dele, a câmera é o botão principal. */
  readonly compact?: boolean | undefined;
  /** Nomeia os campos de arquivo quando a tela tem mais de um par ("Anexar — <label>"). */
  readonly label?: string | undefined;
  readonly onFiles: (files: readonly File[]) => void;
}

/** O dedo é o ponteiro: celular e tablet, onde o `capture` abre a câmera do aparelho. */
function abreACameraDoAparelho(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
}

export function PhotoSourceButtons({
  accept,
  disabled,
  isPending = false,
  compact = false,
  label,
  onFiles,
}: PhotoSourceButtonsProps) {
  const camera = useRef<HTMLInputElement>(null);
  const arquivos = useRef<HTMLInputElement>(null);
  const [webcamAberta, setWebcamAberta] = useState(false);
  const nome = (acao: string) => (label === undefined ? acao : `${acao} — ${label}`);

  const escolhidos = (event: ChangeEvent<HTMLInputElement>) => {
    const files = [...(event.target.files ?? [])];
    event.target.value = '';
    if (files.length > 0) onFiles(files);
  };

  return (
    <>
      {/* Alvo de 44px no dedo: é a ação mais comum, feita com o tablet na mão. */}
      <Button
        type="button"
        variant={compact ? 'secondary' : 'primary'}
        size={compact ? 'sm' : 'md'}
        className={compact ? undefined : 'min-h-11'}
        disabled={disabled}
        onClick={() => {
          if (abreACameraDoAparelho()) camera.current?.click();
          else setWebcamAberta(true);
        }}
      >
        {isPending ? (
          <Loader2 className="animate-spin" aria-hidden="true" />
        ) : (
          <Camera aria-hidden="true" />
        )}
        {isPending ? 'Enviando…' : 'Tirar foto'}
      </Button>

      <Button
        type="button"
        variant="secondary"
        size={compact ? 'sm' : 'md'}
        className={compact ? undefined : 'min-h-11'}
        disabled={disabled}
        onClick={() => arquivos.current?.click()}
      >
        <Paperclip aria-hidden="true" />
        Anexar
      </Button>

      {/* Uma foto por vez, que é o que a câmera devolve. */}
      <input
        ref={camera}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        disabled={disabled}
        aria-label={nome('Tirar foto')}
        onChange={escolhidos}
      />
      {/* Sem `capture`: é o caminho da foto que já está no aparelho, e do PDF do laudo. */}
      <input
        ref={arquivos}
        type="file"
        accept={accept}
        multiple
        className="hidden"
        disabled={disabled}
        aria-label={nome('Anexar')}
        onChange={escolhidos}
      />

      <CameraCaptureDialog
        open={webcamAberta}
        onOpenChange={setWebcamAberta}
        onCapture={(file) => {
          onFiles([file]);
        }}
      />
    </>
  );
}
